import type {
  Communication,
  QueueRecommendation,
  CallStatus,
} from '@entities/communication/model/types';
import type {Employee} from '@entities/employee/model/types';
import type {KpiSnapshot} from './types';
import {MOCK_TODAY} from '@lib/utils';

// ─── Seeded RNG (детерминированные данные) ────────────────────────────────
let seed = 42;
const sr = () => {
  seed = (seed * 1664525 + 1013904223) & 0xffffffff;
  return (seed >>> 0) / 0xffffffff;
};
const rndInt = (min: number, max: number) =>
  Math.floor(sr() * (max - min + 1)) + min;
const rndOf = <T>(arr: T[]): T => arr[Math.floor(sr() * arr.length)];

// ─── Справочники ───────────────────────────────────────────────────────────
const MANAGER_NAMES = [
  'Алексей К.',
  'Мария П.',
  'Иван С.',
  'Ольга Т.',
  'Дмитрий Р.',
  'Наталья В.',
  'Сергей М.',
  'Анна Л.',
];
const MANAGER_IDS = [
  'emp-1',
  'emp-2',
  'emp-3',
  'emp-4',
  'emp-5',
  'emp-6',
  'emp-7',
  'emp-8',
];

const CLIENT_NAMES = [
  'ООО Лидер',
  'ИП Смирнов',
  'АО Прогресс',
  'ООО Техно',
  'ИП Кузнецов',
  'ОАО Меркурий',
  'ООО Старт',
  'ИП Попова',
  'АО Альфа',
  'ООО Бета',
  'ИП Новиков',
  'АО Гамма',
  'ООО Сигма',
  'ИП Козлов',
  'АО Омега',
];
const STATUSES: CallStatus[] = [
  'sale',
  'noresult',
  'noresult',
  'robot',
  'wrongNumber',
];
const NEXT_STEPS = [
  'встреча',
  'перезвон',
  'отправка КП',
  'нет договорённости',
  null,
  null,
  null,
];

// Длинные «маркетинговые» формулировки для колонки `Следующий шаг`.
const NEXT_STEPS_LONG = [
  'Встреча для обсуждения деталей',
  'Начать работу по заказу',
  'Отправить расчёт на email',
  'Клиент позвонит для уточнения готовности',
  'Согласовать условия с руководителем',
  'Прислать коммерческое предложение',
];

const OBJECTION_NAMES = [
  'Дорого',
  'Уже работаем с другими',
  'Нет времени',
  'Нужно подумать',
  'Не интересно',
  'Нет бюджета',
];

// Возражения для колонки `ТИП ВОЗРАЖЕНИЯ` — те же что в InsightsRow на дашборде.
const TARGETED_OBJECTIONS = [
  'Нет потребности',
  'Думаю, посоветуюсь',
  'Уже работаю с другим',
  'Не уверен в качестве',
  'Сейчас не в приоритете',
];

// Краткие резюме нейроаналитика — для колонки `Содержание`.
const SUMMARIES = [
  'Клиент из дилерского центра, интересуется условиями и расчётом стоимости.',
  'Клиент в данный момент занят, попросил перезвонить после часа.',
  'Клиент обсуждает детали заказа, заинтересован в доставке и готов рассмотреть в ближайшие дни.',
  'Клиент уже работает с компанией, подтвердил оплату и готов к продолжению работы.',
  'Клиент монтажник, заинтересован в быстром выполнении заказа. Закупается у текущего поставщика.',
  'Клиент уточняет сроки доставки и условия оплаты.',
  'Клиент готов рассмотреть предложение, попросил выслать материалы.',
];

// Тексты рекомендаций.
const RECOMMENDATIONS = [
  'Зафиксировать договорённость о следующем шаге с конкретной датой.',
  'Уточнить у клиента детали и предложить конкретные условия сотрудничества.',
  'Предложить расчёт для следующего объекта.',
  'Связаться с клиентом для дополнительных вопросов.',
  null,
];

// Причины «нецелевого» звонка — для колонки `Причина`.
const NON_TARGET_REASONS = [
  'Робот на линии',
  'Ошиблись номером',
  'Нет ответа',
  'Клиент сбросил',
  'Перевели на другого специалиста',
];
const REFUSAL_REASONS = [
  'Дорого',
  'Работает с другим поставщиком',
  'Неинтересно',
  'Не устроили сроки',
  'Передумал',
  'Другое',
];
// Причины ред-флагов (ТЗ §2): базовый enum из 9 значений.
const RED_FLAG_REASONS = [
  'Мат или оскорбление',
  'Требование вернуть деньги',
  'Отказ в обслуживании',
  'Отправил разбираться самому',
  'Угроза жалобой, судом или отзывом',
  'Обман или неверные условия',
  'Работа мимо компании',
  'Уход сотрудника',
  'Другое',
];
const CRITERIA_NAMES = [
  'Приветствие',
  'Выявление потребности',
  'Презентация',
  'Работа с возражениями',
  'Закрытие сделки',
  'Следующий шаг',
];

// ─── Employees ────────────────────────────────────────────────────────────
export const MOCK_EMPLOYEES: Employee[] = MANAGER_IDS.map((id, i) => {
  const score = Math.round((5 + sr() * 4) * 10) / 10;
  const calls = rndInt(18, 45);
  return {
    id,
    name: MANAGER_NAMES[i],
    role: 'manager',
    calls,
    callsPlan: 60,
    score,
    scoreDelta: Math.round((sr() * 2 - 1) * 10) / 10,
    conv: rndInt(45, 80),
    convDelta: rndInt(-5, 8),
    // Без лишнего sr(): иначе сдвинется детерминированная последовательность моков.
    objectionsTotal: Math.round(calls * 0.6),
    objIdent: rndInt(70, 98),
    objHandled: rndInt(50, 85),
    scriptCompliance: rndInt(65, 95),
    trend: rndOf(['up', 'down', 'stable']),
    scoreHistory: Array.from(
      {length: 8},
      () => Math.round((5 + sr() * 4) * 10) / 10,
    ),
  };
});

// Шаблонная транскрибация (раунд 27 §8): 8-12 реплик, чтобы аккордеон в карточке
// звонка имел демо-контент. Реплики выбираются псевдо-рандомно из набора.
const TRANSCRIPT_LINES_IN = [
  'Добрый день! Меня зовут %M, компания Колвер. Удобно говорить?',
  'Подскажите, какие задачи сейчас актуальны для вашего отдела?',
  'Я правильно понимаю — вы хотите автоматизировать контроль качества звонков?',
  'У нас есть тариф специально под вашу команду — пришлю расчёт.',
  'Когда вам удобно встретиться, чтобы показать демо?',
  'Отлично, тогда фиксируем — четверг, 14:00. Жду на встрече.',
];
const TRANSCRIPT_LINES_OUT = [
  'Да, слушаю.',
  'У нас сложно с контролем менеджеров, не понятно что происходит на звонках.',
  'Восемь человек в команде.',
  'Пробовали одно решение — но оно дорогое и сложное.',
  'Ну, можно в четверг после обеда.',
  'Договорились. До встречи.',
];

const buildTranscript = (manager: string): Communication['transcript'] => {
  const lines: Communication['transcript'] = [];
  let t = 0;
  const turns = 6 + Math.floor(sr() * 4); // 6–9 пар реплик
  for (let i = 0; i < turns; i++) {
    const mIdx = i % TRANSCRIPT_LINES_IN.length;
    const cIdx = i % TRANSCRIPT_LINES_OUT.length;
    lines.push({
      speaker: 'manager',
      text: TRANSCRIPT_LINES_IN[mIdx].replace('%M', manager.split(' ')[0]),
      time: `${Math.floor(t / 60)}:${(t % 60).toString().padStart(2, '0')}`,
    });
    t += 10 + Math.floor(sr() * 15);
    lines.push({
      speaker: 'client',
      text: TRANSCRIPT_LINES_OUT[cIdx],
      time: `${Math.floor(t / 60)}:${(t % 60).toString().padStart(2, '0')}`,
    });
    t += 8 + Math.floor(sr() * 12);
  }
  return lines;
};

// ─── Коммуникации (140 штук) ───────────────────────────────────────────────
const generateCommunications = (): Communication[] => {
  const result: Communication[] = [];

  for (let i = 1; i <= 140; i++) {
    const managerIdx = rndInt(0, 7);
    const status = rndOf(STATUSES);
    const score = Math.round((3 + sr() * 7) * 10) / 10;
    const isTargeted =
      status === 'sale' || (status === 'noresult' && sr() > 0.4);
    const daysAgo = rndInt(0, 30);
    const date = new Date(MOCK_TODAY);
    date.setDate(date.getDate() - daysAgo);
    date.setHours(rndInt(9, 18), rndInt(0, 59));

    const criteria: Communication['criteria'] = CRITERIA_NAMES.map((name) => ({
      name,
      score: Math.round((2 + sr() * 8) * 10) / 10,
    }));

    const objections = OBJECTION_NAMES.slice(0, rndInt(2, 4));
    const objectionsHandled = objections.filter(() => sr() > 0.4);

    // Исход звонка — 4 строго вложенных уровня (§1):
    //   saleMade → goalAchieved → agreementReached → result ≠ null
    let saleMade = false;
    let goalAchieved = false;
    let agreementReached = false;
    let resultDisplay: string | null = null;
    let resultText: string | null = null;
    let refusalReason: string | null = null;

    if (isTargeted) {
      if (status === 'sale') {
        saleMade = sr() > 0.55;
        goalAchieved = saleMade || sr() > 0.3;
      } else {
        saleMade = false;
        goalAchieved = sr() > 0.25;
      }
      agreementReached = goalAchieved || sr() > 0.2;
      if (agreementReached) {
        resultDisplay = saleMade
          ? 'Продажа'
          : goalAchieved
            ? 'Договорились'
            : 'Назначен следующий звонок';
        resultText = resultDisplay;
        refusalReason = null;
      } else {
        if (sr() > 0.5) {
          resultDisplay = 'Отказ';
          resultText = 'Клиент отказался';
          refusalReason = rndOf(REFUSAL_REASONS);
        } else {
          resultDisplay = null;
          resultText = null;
          refusalReason = null;
        }
      }
    }

    const temperature: Communication['temperature'] = !isTargeted
      ? 'cold'
      : sr() > 0.7
        ? 'hot'
        : sr() > 0.4
          ? 'warm'
          : 'cold';

    const withoutConflict = !(isTargeted && score < 4 && sr() > 0.7);
    const conflictSource: Communication['conflictSource'] = withoutConflict
      ? null
      : rndOf(['Клиент', 'Менеджер', 'Обоюдный']);

    // Ред-флаг — редко, независимо от негатива/счёта (ТЗ §4: не смешивается, не влияет на балл).
    // ~5% звонков; каждый ~40-й — с неизвестным значением причины (ТЗ 3.1: как есть).
    const redFlag = sr() > 0.95;
    const redFlagReason: Communication['redFlagReason'] = redFlag
      ? sr() > 0.975
        ? 'Эскалация в чат поддержки'
        : rndOf(RED_FLAG_REASONS)
      : null;

    const hasObjection = isTargeted && sr() > 0.35;
    const mainObjection = hasObjection ? rndOf(TARGETED_OBJECTIONS) : null;

    result.push({
      id: `C-${1000 + i}`,
      dateTime: date.toISOString(),
      managerId: MANAGER_IDS[managerIdx],
      manager: MANAGER_NAMES[managerIdx],
      client: rndOf(CLIENT_NAMES),
      phone: `+7 9${rndInt(10, 99)} ${rndInt(100, 999)}-${rndInt(10, 99)}-${rndInt(10, 99)}`,
      duration: rndInt(60, 600),
      score,
      isTargeted,
      isPrimary: sr() > 0.45,
      direction: rndOf(['in', 'out']),
      channelType: rndOf([
        'phone',
        'phone',
        'phone',
        'phone',
        'meeting',
        'meeting',
        'meeting',
      ] as const),
      answered: sr() > 0.15,
      status,
      nextStep: isTargeted ? rndOf(NEXT_STEPS) : null,
      queueItem: null,
      transcript: buildTranscript(MANAGER_NAMES[managerIdx]),
      criteria,
      objections,
      objectionsHandled,
      analysisRunId: `run-${1000 + i}`,
      result: resultDisplay,
      resultText,
      saleMade,
      goalAchieved,
      agreementReached,
      withoutConflict,
      conflictSource,
      redFlag,
      redFlagReason,
      refusalReason,
      temperature,
      mainObjection,
      scriptCompliance: sr() > 0.35,
      promoApplied: sr() > 0.55,
      summary: isTargeted
        ? rndOf(SUMMARIES)
        : 'Клиент сказал, что ошиблись номером.',
      recommendation: isTargeted ? rndOf(RECOMMENDATIONS) : null,
      nonTargetReason: isTargeted ? null : rndOf(NON_TARGET_REASONS),
    });
    // Тип встречи — по номеру, без лишнего вызова ГСЧ (остальные моки не сдвигаются).
    const comm = result[result.length - 1];
    if (comm.channelType === 'meeting') {
      comm.meetingType = i % 2 === 0 ? 'offline' : 'online';
    }
  }

  return result;
};

export const MOCK_COMMUNICATIONS = generateCommunications();

// ─── Детальный звонок C-1841 ──────────────────────────────────────────────
export const MOCK_DETAILED_CALL: Communication = {
  id: 'C-1841',
  dateTime: `${MOCK_TODAY}T15:30:00.000Z`,
  managerId: 'emp-1',
  manager: 'Алексей К.',
  client: 'ООО Лидер',
  phone: '+7 916 123-45-67',
  duration: 165,
  score: 7.8,
  isTargeted: true,
  isPrimary: true,
  direction: 'out',
  answered: true,
  status: 'sale',
  nextStep: 'встреча',
  queueItem: null,
  analysisRunId: 'run-1841',
  transcript: [
    {
      speaker: 'manager',
      text: 'Добрый день! Это Алексей из компании Колвер. Удобно говорить?',
      time: '0:00',
    },
    {speaker: 'client', text: 'Да, слушаю вас.', time: '0:05'},
    {
      speaker: 'manager',
      text: 'Отлично! Я звоню по поводу нашего предложения по оптимизации процессов продаж. Расскажите, какие задачи сейчас актуальны для вашего отдела?',
      time: '0:08',
    },
    {
      speaker: 'client',
      text: 'Ну, в принципе, есть сложности с контролем менеджеров. Не всегда понятно, что происходит на звонках.',
      time: '0:20',
    },
    {
      speaker: 'manager',
      text: 'Понимаю вас. Именно с этим мы помогаем — автоматический анализ каждого звонка, оценка по критериям, рекомендации по улучшению. Сколько менеджеров у вас в команде?',
      time: '0:35',
    },
    {speaker: 'client', text: 'Восемь человек.', time: '0:52'},
    {
      speaker: 'manager',
      text: 'Отлично. Для восьми менеджеров у нас есть специальный тариф. Скажите, вы уже пробовали какие-то инструменты для контроля качества звонков?',
      time: '0:55',
    },
    {
      speaker: 'client',
      text: 'Пробовали одно решение, но оно было слишком дорогим и сложным.',
      time: '1:10',
    },
    {
      speaker: 'manager',
      text: 'Это частая история. Наш продукт, наоборот, простой в настройке — запускается за один день. По цене — давайте я пришлю расчёт конкретно под вас. Когда удобно встретиться, чтобы показать демо?',
      time: '1:18',
    },
    {speaker: 'client', text: 'Ну, можно в четверг после обеда.', time: '1:45'},
    {
      speaker: 'manager',
      text: 'Отлично! Записываю — четверг, 14:00. Пришлю приглашение на вашу почту. Спасибо за время!',
      time: '1:50',
    },
    {speaker: 'client', text: 'Договорились.', time: '2:00'},
    {speaker: 'manager', text: 'До встречи!', time: '2:03'},
  ],
  criteria: [
    {name: 'Приветствие', score: 9.0},
    {name: 'Выявление потребности', score: 8.5},
    {name: 'Презентация', score: 7.0},
    {name: 'Работа с возражениями', score: 6.5},
    {name: 'Закрытие сделки', score: 8.0},
    {name: 'Следующий шаг', score: 9.5},
  ],
  objections: ['Дорого', 'Сложно внедрить', 'Уже есть решение'],
  objectionsHandled: ['Дорого', 'Сложно внедрить'],
  result: 'Продажа',
  resultText: 'Клиент согласился на встречу и демо',
  saleMade: true,
  goalAchieved: true,
  agreementReached: true,
  withoutConflict: true,
  conflictSource: null,
  redFlag: null,
  redFlagReason: null,
  refusalReason: null,
  temperature: 'hot',
  mainObjection: 'Дорого',
  scriptCompliance: true,
  promoApplied: true,
  summary:
    'Клиент уточнил формат демо и согласовал встречу на четверг. Менеджер чётко зафиксировал договорённость.',
  recommendation:
    'Подтвердить встречу за день до — отправить календарное приглашение и материалы.',
  nonTargetReason: null,
};

MOCK_COMMUNICATIONS.unshift(MOCK_DETAILED_CALL);

// ─── Queue Items ──────────────────────────────────────────────────────────
export const MOCK_QUEUE_ITEMS: QueueRecommendation[] = [
  {
    id: 'q-001',
    communicationId: 'C-1023',
    priority: 1,
    queueType: 'urgent',
    problem: 'Грубость с клиентом',
    client: 'АО Прогресс',
    managerId: 'emp-3',
    manager: 'Иван С.',
    age: '2 часа назад',
    recommendation: 'Провести срочный разбор. Клиент оставил жалобу.',
    score: 3.2,
    direction: 'in',
  },
  {
    id: 'q-002',
    communicationId: 'C-1045',
    priority: 1,
    queueType: 'urgent',
    problem: 'Нарушение скрипта продаж',
    client: 'ООО Техно',
    managerId: 'emp-5',
    manager: 'Дмитрий Р.',
    age: '4 часа назад',
    recommendation:
      'Критические отклонения от скрипта. Требует немедленного внимания.',
    score: 3.8,
    direction: 'out',
  },
  {
    id: 'q-003',
    communicationId: 'C-1067',
    priority: 2,
    queueType: 'urgent',
    problem: 'Не закрыта сделка при высоком интересе клиента',
    client: 'ИП Смирнов',
    managerId: 'emp-2',
    manager: 'Мария П.',
    age: '1 день назад',
    recommendation: 'Разобрать технику закрытия. Клиент был готов.',
    score: 5.5,
    direction: 'out',
  },
  {
    id: 'q-004',
    communicationId: 'C-1089',
    priority: 2,
    queueType: 'urgent',
    problem: 'Возражения не обработаны',
    client: 'ООО Старт',
    managerId: 'emp-4',
    manager: 'Ольга Т.',
    age: '1 день назад',
    recommendation: 'Проработать скрипт обработки возражения "Дорого".',
    score: 4.9,
    direction: 'out',
  },
  {
    id: 'q-005',
    communicationId: 'C-1101',
    priority: 3,
    queueType: 'urgent',
    problem: 'Затяжной звонок без результата',
    client: 'ИП Попова',
    managerId: 'emp-7',
    manager: 'Сергей М.',
    age: '2 дня назад',
    recommendation: 'Разобрать тайм-менеджмент звонка.',
    score: 5.1,
    direction: 'in',
  },
  {
    id: 'm-001',
    communicationId: 'C-1115',
    priority: 1,
    queueType: 'management',
    problem: 'Клиент запрашивает нестандартные условия',
    client: 'АО Альфа',
    managerId: 'emp-1',
    manager: 'Алексей К.',
    age: '3 часа назад',
    recommendation: 'Необходимо согласование РОПа для продолжения переговоров.',
    score: 7.2,
    direction: 'out',
  },
  {
    id: 'm-002',
    communicationId: 'C-1130',
    priority: 2,
    queueType: 'management',
    problem: 'Крупный потенциальный клиент',
    client: 'ОАО Меркурий',
    managerId: 'emp-6',
    manager: 'Наталья В.',
    age: '1 день назад',
    recommendation: 'Сделка >500к. Рекомендуется личное участие РОПа.',
    score: 8.1,
    direction: 'out',
  },
  {
    id: 'm-003',
    communicationId: 'C-1145',
    priority: 3,
    queueType: 'management',
    problem: 'Клиент просит скидку выше лимита менеджера',
    client: 'ООО Бета',
    managerId: 'emp-3',
    manager: 'Иван С.',
    age: '2 дня назад',
    recommendation: 'Одобрить скидку 15% или предложить альтернативу.',
    score: 6.8,
    direction: 'out',
  },
  {
    id: 'p-001',
    communicationId: 'C-1160',
    priority: 2,
    queueType: 'practices',
    problem: 'Образцовая обработка возражения "Дорого"',
    client: 'АО Гамма',
    managerId: 'emp-8',
    manager: 'Анна Л.',
    age: '3 дня назад',
    recommendation: 'Отличный пример — поделиться с командой на разборе.',
    score: 9.4,
    direction: 'out',
  },
  {
    id: 'p-002',
    communicationId: 'C-1175',
    priority: 3,
    queueType: 'practices',
    problem: 'Быстрое закрытие сделки',
    client: 'ООО Сигма',
    managerId: 'emp-2',
    manager: 'Мария П.',
    age: '4 дня назад',
    recommendation: 'Техника закрытия за 2 минуты — тиражировать.',
    score: 9.1,
    direction: 'out',
  },
];

// ─── KPI по периодам ──────────────────────────────────────────────────────
const makeKpi = (period: KpiSnapshot['period']): KpiSnapshot => ({
  period,
  callsTotal: rndInt(120, 250),
  callsPlan: 300,
  callsDelta: rndInt(-15, 25),
  avgScore: Math.round((6 + sr() * 2) * 10) / 10,
  scoreDelta: Math.round((sr() * 1.2 - 0.4) * 10) / 10,
  conversion: rndInt(42, 68),
  convDelta: rndInt(-4, 8),
  withResult: rndInt(55, 75),
  withResultDelta: rndInt(-3, 6),
  withAgreement: rndInt(30, 50),
  withAgreementDelta: rndInt(-5, 7),
  scoreHistory: Array.from(
    {length: 8},
    () => Math.round((5.5 + sr() * 3) * 10) / 10,
  ),
});

export const MOCK_KPI: Record<string, KpiSnapshot> = {
  day: makeKpi('day'),
  week: makeKpi('week'),
  month: makeKpi('month'),
  quarter: makeKpi('quarter'),
  year: makeKpi('year'),
};

// ─── Notifications ────────────────────────────────────────────────────────

/** 6 категорий уведомлений (раунд 10 §2.3.1). */
export type NotificationKind =
  | 'priority-1' // красный — критическое нарушение в звонке
  | 'alert' // оранжевый — тренд/метрика в красной зоне
  | 'digest' // синий — периодическая сводка
  | 'practice' // фиолетовый — лучшая практика
  | 'achievement' // зелёный — план/задача выполнена
  | 'report'; // серый — еженедельный/месячный отчёт

export interface NotificationManager {
  initials: string;
  name: string;
}

export interface MockNotification {
  id: string;
  type: NotificationKind;
  unread: boolean;
  /** Относительная строка времени, готовая к отображению. */
  time: string;
  title: string;
  desc: string;
  manager?: NotificationManager;
  callId?: number;
}

export const MOCK_NOTIFICATIONS: MockNotification[] = [
  {
    id: 'NF1',
    type: 'priority-1',
    unread: true,
    time: '5 мин назад',
    title: 'Возражение по цене не отработано',
    desc: 'Менеджер пропустил ключевые шаги скрипта на этапе работы с ценой.',
    manager: {initials: 'СА', name: 'Сидоров А.К.'},
    callId: 1841,
  },
  {
    id: 'NF2',
    type: 'priority-1',
    unread: true,
    time: '18 мин назад',
    title: 'Не зафиксирован следующий шаг',
    desc: 'Звонок завершён без договорённости о следующем действии.',
    manager: {initials: 'КМ', name: 'Козлов М.О.'},
    callId: 1842,
  },
  {
    id: 'NF3',
    type: 'digest',
    unread: false,
    time: '30 мин назад',
    title: 'Дайджест: 2 новых звонка в очереди',
    desc: 'Приоритет 2 — звонки #1843 и #1844 ожидают разбора.',
  },
  {
    id: 'NF4',
    type: 'alert',
    unread: true,
    time: '45 мин назад',
    title: 'Снижение конверсии',
    desc: 'Конверсия отдела упала на 4% за последние 3 дня.',
  },
  {
    id: 'NF5',
    type: 'priority-1',
    unread: false,
    time: '1 ч назад',
    title: 'Скрипт не соблюдён',
    desc: 'Менеджер отклонился от структуры звонка на этапе выявления потребности.',
    manager: {initials: 'ПИ', name: 'Петров И.С.'},
    callId: 1839,
  },
  {
    id: 'NF6',
    type: 'digest',
    unread: false,
    time: '1 ч 30 мин назад',
    title: 'Дайджест: 3 звонка обработано за час',
    desc: 'Обработано: обратная связь ×2, разбор назначен ×1.',
  },
  {
    id: 'NF7',
    type: 'practice',
    unread: false,
    time: '2 ч назад',
    title: 'Найдена лучшая практика',
    desc: 'Анна Л. показала отличную работу с возражением «дорого» — 9.4 балла.',
    manager: {initials: 'АЛ', name: 'Анна Л.'},
    callId: 1820,
  },
  {
    id: 'NF8',
    type: 'achievement',
    unread: false,
    time: '3 ч назад',
    title: 'План выполнен',
    desc: 'Мария П. выполнила недельный план на 105%.',
    manager: {initials: 'МП', name: 'Мария П.'},
  },
  {
    id: 'NF9',
    type: 'achievement',
    unread: false,
    time: '5 ч назад',
    title: 'Задача закрыта',
    desc: 'Иван С. закрыл задачу «Разбор звонка с ООО Техно».',
    manager: {initials: 'ИС', name: 'Иван С.'},
  },
  {
    id: 'NF10',
    type: 'report',
    unread: false,
    time: 'вчера',
    title: 'Еженедельный отчёт',
    desc: 'Отчёт за неделю сформирован и готов к просмотру.',
  },
];
